// The bind groups of the WebGPU backend (the runtime design §3).
//
// WebGL2 binds a texture to a unit and the program reads it by name. WebGPU
// binds a GROUP, and a group must match the pipeline's layout exactly. So the
// backend declares THREE layouts and never `layout: 'auto'`:
//
//  * `world` carries every plane unit, so the terrain pass and the territory
//    pass share one group even though each reads a subset;
//  * `sprite` carries the atlas alone, for the three instanced batches;
//  * `none` carries no texture at all, for the background and the vignette.
//
// A layout that a shader under-declares is legal: an entry nothing reads
// costs nothing. A layout built from what one shader happens to declare is
// not shareable at all, which is why the three are fixed.
//
// A unit with nothing bound still needs a view, because the group is
// complete or it is invalid. Such a unit gets a 1x1 PLACEHOLDER of the right
// kind: an integer plane reads zero and a float plane reads black.

import {
  BINDING_SAMPLER_ATLAS, BINDING_SAMPLER_LINEAR, BINDING_UNIFORMS,
  TEXTURE_UNITS, type BindGroupKind,
} from '../shaders/wgsl/bindings';
import { STAGE_FRAGMENT, STAGE_VERTEX, TEXTURE_BINDING, TEXTURE_COPY_DST } from './flags';
import type { UniformArena } from './arena';

/** One slot of the units table: what is bound, and which texture it is. */
export interface UnitSlot {
  view: GPUTextureView | null;
  /** The texture's id. It keys the group cache; 0 means nothing is bound. */
  id: number;
}

/** Writes one texture into the units table: the one way a plane or a sheet binds. */
export function bindUnit(
  units: readonly UnitSlot[], unit: number, view: GPUTextureView, id: number,
): void {
  const slot = units[unit];
  if (!slot) return;
  slot.view = view;
  slot.id = id;
}

/**
 * The units a world group reads as INTEGER planes.
 *
 * They are `uOwners` (0), `uBlocked` (1), `uSurvey` (9, R2626) and
 * `uDepth` (10, R2713) of
 * `gfx/worldquad.ts` `SAMPLER_UNITS`. An integer texture is never filterable
 * and a shader reads it with `textureLoad`, so its layout entry states
 * `sampleType: 'uint'` and its placeholder is `r8uint`.
 */
export const UINT_UNITS: ReadonlySet<number> = new Set([0, 1, 9, 10]);

/** Does this unit of this layout hold an integer plane? The atlas never does. */
const isUintUnit = (kind: BindGroupKind, unit: number) =>
  kind === 'world' && UINT_UNITS.has(unit);

/** Which units each layout carries. `sprite` holds the atlas on unit 0. */
export function unitsOf(kind: BindGroupKind): readonly number[] {
  if (kind === 'world') return WORLD_UNITS;
  return kind === 'sprite' ? SPRITE_UNITS : EMPTY_UNITS;
}

const WORLD_UNITS: readonly number[] = Array.from({ length: TEXTURE_UNITS }, (_, i) => i);
const SPRITE_UNITS: readonly number[] = [0];
const EMPTY_UNITS: readonly number[] = [];

/** The key of one group-1 cache entry: the ids of the units it reads. */
export function groupKey(
  kind: BindGroupKind, units: readonly UnitSlot[],
): string {
  // A draw asks for the key, so it is one string and no array (§10).
  const list = unitsOf(kind);
  let key = `${kind}:`;
  for (let i = 0; i < list.length; i++) {
    if (i > 0) key += ',';
    key += units[list[i]]?.id ?? 0;
  }
  return key;
}

/** Whether the units still hold the ids `ids` names, in the order of `list`. */
function sameIds(ids: readonly number[], list: readonly number[], units: readonly UnitSlot[]): boolean {
  for (let i = 0; i < list.length; i++) {
    if (ids[i] !== (units[list[i]]?.id ?? 0)) return false;
  }
  return true;
}

/**
 * Every bind-group layout of the backend, and the caches over them.
 *
 * It is a GPU object like any other: a lost device destroys it and the
 * restore builds it again. The caches are dropped with it, because a bind
 * group belongs to the device that made it.
 */
export class BindGroups {
  private device: GPUDevice | null = null;
  private shared: GPUBindGroupLayout | null = null;
  private textures = new Map<BindGroupKind, GPUBindGroupLayout | null>();
  private pipelines = new Map<BindGroupKind, GPUPipelineLayout>();
  private groupOne = new Map<string, GPUBindGroup>();
  /**
   * The last group-1 answer of each kind and the unit ids it was made for.
   * A draw that binds what the last draw of its kind bound compares the ids
   * and builds no key string (§10).
   */
  private lastOne = new Map<BindGroupKind, { ids: number[]; group: GPUBindGroup }>();
  /** Group 0 by slice size, for the arena generation `zeroGeneration`. */
  private groupZero = new Map<number, GPUBindGroup>();
  private zeroGeneration = -1;
  /** The two samplers of group 0, and the two placeholder views. */
  private linear: GPUSampler | null = null;
  private atlas: GPUSampler | null = null;
  private blankUint: GPUTexture | null = null;
  private blankFloat: GPUTexture | null = null;
  private blankUintView: GPUTextureView | null = null;
  private blankFloatView: GPUTextureView | null = null;

  constructor(
    private readonly arena: UniformArena,
    private readonly units: readonly UnitSlot[],
  ) {}

  create(device: GPUDevice): void {
    this.destroy();
    this.device = device;

    // The plane sampler: clamp and linear, and no mip at all, because a data
    // plane carries one level. The atlas sampler is trilinear, because the
    // sprite sheet carries four (R548, `gl/texture.ts` ATLAS_MAX_LEVEL).
    this.linear = device.createSampler({
      label: 'fast: sLinear',
      addressModeU: 'clamp-to-edge', addressModeV: 'clamp-to-edge',
      magFilter: 'linear', minFilter: 'linear',
    });
    this.atlas = device.createSampler({
      label: 'fast: sAtlas',
      addressModeU: 'clamp-to-edge', addressModeV: 'clamp-to-edge',
      magFilter: 'linear', minFilter: 'linear', mipmapFilter: 'linear',
    });

    this.blankUint = device.createTexture({
      label: 'fast: blank uint plane',
      size: [1, 1], format: 'r8uint', usage: TEXTURE_BINDING | TEXTURE_COPY_DST,
    });
    this.blankFloat = device.createTexture({
      label: 'fast: blank float plane',
      size: [1, 1], format: 'rgba16float', usage: TEXTURE_BINDING | TEXTURE_COPY_DST,
    });
    this.blankUintView = this.blankUint.createView();
    this.blankFloatView = this.blankFloat.createView();

    this.shared = device.createBindGroupLayout({
      label: 'fast: group 0',
      entries: [
        {
          binding: BINDING_UNIFORMS,
          visibility: STAGE_VERTEX | STAGE_FRAGMENT,
          buffer: { type: 'uniform', hasDynamicOffset: true },
        },
        {
          binding: BINDING_SAMPLER_LINEAR,
          visibility: STAGE_FRAGMENT,
          sampler: { type: 'filtering' },
        },
        {
          binding: BINDING_SAMPLER_ATLAS,
          visibility: STAGE_FRAGMENT,
          sampler: { type: 'filtering' },
        },
      ],
    });

    for (const kind of ['world', 'sprite', 'none'] as const) {
      const units = unitsOf(kind);
      if (units.length === 0) {
        this.textures.set(kind, null);
        this.pipelines.set(kind, device.createPipelineLayout({
          label: 'fast: layout none', bindGroupLayouts: [this.shared],
        }));
        continue;
      }
      const layout = device.createBindGroupLayout({
        label: `fast: group 1 ${kind}`,
        entries: units.map((u) => ({
          binding: u,
          visibility: STAGE_FRAGMENT,
          texture: { sampleType: isUintUnit(kind, u) ? 'uint' as const : 'float' as const },
        })),
      });
      this.textures.set(kind, layout);
      this.pipelines.set(kind, device.createPipelineLayout({
        label: `fast: layout ${kind}`, bindGroupLayouts: [this.shared, layout],
      }));
    }
  }

  destroy(): void {
    if (this.blankUint) this.blankUint.destroy();
    if (this.blankFloat) this.blankFloat.destroy();
    this.blankUint = null;
    this.blankFloat = null;
    this.blankUintView = null;
    this.blankFloatView = null;
    this.linear = null;
    this.atlas = null;
    this.shared = null;
    this.textures.clear();
    this.pipelines.clear();
    this.groupOne.clear();
    this.lastOne.clear();
    this.groupZero.clear();
    this.device = null;
  }

  /** The pipeline layout one program is built against. */
  pipelineLayout(kind: BindGroupKind): GPUPipelineLayout {
    const held = this.pipelines.get(kind);
    if (!held) throw new Error(`fast: no pipeline layout for ${kind}`);
    return held;
  }

  /**
   * The group-0 bind group for one block size.
   *
   * The slice must be at least as wide as the shader's struct, and a world
   * block is 288 bytes, so the size is the block rounded to the slot. The
   * cache holds the arena's generation as well, because a grown arena is a
   * NEW buffer and every group over the old one is stale; a new generation
   * empties it. The key is the size alone, so a draw builds no string.
   */
  group0(sliceBytes: number): GPUBindGroup | null {
    const device = this.device;
    const buffer = this.arena.buffer;
    if (!device || !buffer || !this.shared || !this.linear || !this.atlas) return null;
    if (this.zeroGeneration !== this.arena.generation) {
      this.groupZero.clear();
      this.zeroGeneration = this.arena.generation;
    }
    const held = this.groupZero.get(sliceBytes);
    if (held) return held;
    const made = device.createBindGroup({
      label: 'fast: group 0',
      layout: this.shared,
      entries: [
        { binding: BINDING_UNIFORMS, resource: { buffer, offset: 0, size: sliceBytes } },
        { binding: BINDING_SAMPLER_LINEAR, resource: this.linear },
        { binding: BINDING_SAMPLER_ATLAS, resource: this.atlas },
      ],
    });
    this.groupZero.set(sliceBytes, made);
    return made;
  }

  /**
   * The group-1 bind group for what is bound NOW.
   *
   * The key is the concatenated texture ids, so a picture that binds the same
   * planes every frame builds the group once and a grid change builds a new
   * one. A unit with nothing bound takes the placeholder of its kind.
   */
  group1(kind: BindGroupKind): GPUBindGroup | null {
    const device = this.device;
    const layout = this.textures.get(kind) ?? null;
    if (!device || !layout) return null;
    const list = unitsOf(kind);
    const last = this.lastOne.get(kind);
    if (last && sameIds(last.ids, list, this.units)) return last.group;
    const key = groupKey(kind, this.units);
    let group = this.groupOne.get(key);
    if (!group) {
      const entries: GPUBindGroupEntry[] = [];
      for (const u of list) {
        const slot = this.units[u];
        const blank = isUintUnit(kind, u) ? this.blankUintView : this.blankFloatView;
        const view = slot?.view ?? blank;
        if (!view) return null;
        entries.push({ binding: u, resource: view });
      }
      group = device.createBindGroup({ label: `fast: group 1 ${kind}`, layout, entries });
      this.groupOne.set(key, group);
    }
    const ids = last?.ids ?? new Array<number>(list.length);
    for (let i = 0; i < list.length; i++) ids[i] = this.units[list[i]]?.id ?? 0;
    if (last) last.group = group;
    else this.lastOne.set(kind, { ids, group });
    return group;
  }

  /**
   * Drops every cached group-1 entry.
   *
   * A texture that was destroyed and rebuilt keeps its id only while it
   * lives; the context gives a new id on every create, so a stale group can
   * never be found by key. This call exists for the restore, which rebuilds
   * the layouts the cached groups point at.
   */
  invalidate(): void {
    this.groupOne.clear();
    this.lastOne.clear();
    this.groupZero.clear();
  }
}
